/**
 * Route map for all potential react-router or client-side supported routes in all of our apps.
 * Also provides helper functions to generate the appropriate url's for the appropriate scenarios
 *
 * Copyright (c) 2019 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
const RouteMap = {
  account: {
    url: '/account',
    login: '/login',
    signup: '/sign-up',
    feedback: '/feedback',
    passwordReset: '/password-reset',
    setPassword: '/set-password',
    admin: '/admin'
  },
  home: {
    url: '/home'
  },
  board: {
    url: '/board',
    place: '/place',
    listPlace: '/listPlace',
    public: {
      url: '/p',
      listPlace: '/listPlace',
      hotel: 'hotel'
    },
    gallery: '/gallery',
    map: '/map',
    new: '/new',
    guest: '/guest',
    hotel: '/hotel'
  },
  settings: {
    url: '/settings'
  },
  public: {
    url: '/p'
  },
  embed: {
    url: '/embed'
  },
  notifications: {
    url: '/notifications'
  },
  explore: {
    url: '/explore',
    destinations: '/destinations',
    trips: '/trips',
    airbnb: '/airbnb'
  },
  quiz: {
    url: '/quiz',
    personality: {
      url: '/personality',
      questions: '/questions',
      types: '/types',
      share: '/share',
      save: '/save'
    }
  },
  sitemap: {
    url: '/sitemap',
    locations: {
      url: '/locations'
    }
  }
}

/**
 * Returns the param value for the provided key from the urlParams object
 * @param {Object} match The react-router match object
 */
export const getParamValue = (key, match) => {
  const urlParams = match.params || {}

  return urlParams[key] || null
}

/**
 * Returns the boolean that indicates whether the specified path is in the current match path
 * @param {Object} match The react-router match object
 */
export const includesPath = (path, match) => {
  const matchPaths = match.path || ''

  return matchPaths.includes(path)
}

/**
 * Returns a boolean that specifies whether the current page or provided route is an embed route
 */
export const isEmbedRoute = () => {
  return window.location.pathname.startsWith(RouteMap.embed.url)
}

/**
 * Returns a boolean that specifies whether the current page or provided route is a guest board route
 */
export const isGuestBoardRoute = () => {
  return window.location.pathname.startsWith(`${RouteMap.board.url}${RouteMap.board.guest}`)
}

/**
 * Returns the boolean that indicates whether or not the board drawer is open by checking the following:
 * 1. board details drawer
 * 2. listPlace details drawer
 * @param {Object} match The react-router match object
 */
export const isBoardDrawerOpen = (match) => {
  const { params = {} } = match

  const isBoardDetailsDrawerOpen = !!params.boardId && !!params.sharedBoardId
  const isListPlaceDetailsDrawerOpen = !!params.listPlaceId

  return isBoardDetailsDrawerOpen || isListPlaceDetailsDrawerOpen
}

/**
 * Determines whether to use the public board url path or the regular board path
 * This is determined by interpreting the react-router match.params object
 * See: https://github.com/ReactTraining/react-router/blob/master/packages/react-router/docs/api/match.md
 * @param {Object} params The react-router match params object
 */
export const getBoardRoute = (params = {}) => {
  // TODO: Explore cleaner way to check for embed
  if (isEmbedRoute()) {
    return getEmbedRoute(params.sharedBoardId)
  }

  if (isGuestBoardRoute()) {
    return getGuestBoardRoute()
  }

  if (params.sharedBoardId) {
    return getShareBoardRoute(params.sharedBoardId)
  } else if (params.boardId) {
    return `${RouteMap.board.url}/${params.boardId}`
  }

  return '/'
}

/**
 * Returns the public shared board route
 * @param {String} sharedBoardId The sharedBoardId
 */
export const getShareBoardRoute = (sharedBoardId) => {
  if (sharedBoardId) {
    return `${RouteMap.public.url}/${sharedBoardId}`
  }

  return '/'
}

/**
 * Returns the guest board route
 */
export const getGuestBoardRoute = () => {
  return `${RouteMap.board.url}${RouteMap.board.guest}`
}

/**
 * Returns the public share url
 * @param {String} sharedBoardId The sharedBoardId
 */
export const getShareBoardUrl = (sharedBoardId) => {
  return (sharedBoardId) ? `${__MAIN_URL__}${getShareBoardRoute(sharedBoardId)}` : null
}

/**
 * Returns the embed route
 * @param {String} sharedBoardId The sharedBoardId
 */
export const getEmbedRoute = (sharedBoardId, query = '') => {
  const formattedQuery = query ? `?${query}` : ''

  if (sharedBoardId) {
    return `${RouteMap.embed.url}/${sharedBoardId}${formattedQuery}`
  }

  return '/'
}

/**
 * Returns the embed iframe src url
 * @param {String} sharedBoardId The sharedBoardId
 */
export const getEmbedUrl = (sharedBoardId, query = '') => {
  return (sharedBoardId) ? `${__MAIN_URL__}${getEmbedRoute(sharedBoardId, query)}` : null
}

/**
 * Returns the gallery route depending on whether we want the public, sharable route or the regular route
 * This is determined by interpreting the react-router match object
 * @param {Object} params The react-router match params object
 */
export const getGalleryRoute = (params) => {
  const boardRoute = getBoardRoute(params)

  return `${boardRoute}${RouteMap.board.gallery}`
}

/**
 * Returns the map route depending on whether we want the public, sharable route or the regular route
 * This is determined by interpreting the react-router match object
 * @param {Object} params The react-router match params object
 */
export const getMapRoute = (params) => {
  const boardRoute = getBoardRoute(params)

  return `${boardRoute}${RouteMap.board.map}`
}

/**
 * Returns the board listPlace route
 * @param {Object} params The react-router match params object
 * @param {String} lpid The list place id
 */
export const getBoardListPlaceRoute = (params, lpid) => {
  const boardRoute = getBoardRoute(params)

  return `${boardRoute}${RouteMap.board.listPlace}/${lpid}`
}

/**
 * Returns the board place route
 * @param {Object} params The react-router match params object
 * @param {String} pid The place id (google place id)
 */
export const getBoardPlaceRoute = (params, pid) => {
  const boardRoute = getBoardRoute(params)

  return `${boardRoute}${RouteMap.board.place}/${pid}`
}

/**
 * Returns the board hotel route
 * @param {Object} params The react-router match params object
 * @param {String} taid The trip advisor id
 */
export const getBoardHotelRoute = (params, taid) => {
  const boardRoute = getBoardRoute(params)

  return `${boardRoute}${RouteMap.board.hotel}/${taid}`
}

/**
 * Returns the board public board route
 * @param {Object} params The react-router match params object
 * @param {String} sharedBoardId The public board id
 */
export const getBoardPublicBoardRoute = (params, sharedBoardId) => {
  const boardRoute = getBoardRoute(params)

  return `${boardRoute}${RouteMap.board.public.url}/${sharedBoardId}`
}

/**
 * Returns the board public board listPlace route
 * @param {Object} params The react-router match params object
 * @param {String} sharedBoardId The public board id
 * @param {String} listPlaceId The list place id
 */
export const getBoardPublicBoardListPlaceRoute = (params, sharedBoardId, listPlaceId) => {
  const boardRoute = getBoardRoute(params)

  return `${boardRoute}${RouteMap.board.public.url}/${sharedBoardId}${RouteMap.board.public.listPlace}/${listPlaceId}`
}

/**
 * Returns the url for sharing a public gallery
 * @param {String} sharedBoardId The shared board id
 */
export const getShareGalleryUrl = (sharedBoardId) => {
  const shareBoardUrl = getShareBoardUrl(sharedBoardId)

  return (shareBoardUrl) ? `${shareBoardUrl}${RouteMap.board.gallery}` : null
}

/**
 * Returns the url for sharing a public place url
 * @param {String} sharedBoardId The shared board id
 * @param {String} lpid The list place id
 */
export const getSharePlaceUrl = (sharedBoardId, lpid) => {
  const shareBoardUrl = getShareBoardUrl(sharedBoardId)

  return (shareBoardUrl) ? `${shareBoardUrl}${RouteMap.board.listPlace}/${lpid}` : null
}

/**
 * Returns the explore destination url for the provided destination
 * @param {String} gpid The destination gpid
 */
export const getCityDestinationUrl = (gpid) => {
  return `${RouteMap.explore.url}${RouteMap.explore.destinations}/${gpid}`
}

/**
 * Returns the url for the personality quiz questions
 */
export const getQuizQuestionsUrl = () => {
  return `${RouteMap.quiz.url}${RouteMap.quiz.personality.url}${RouteMap.quiz.personality.questions}`
}

/**
 * Returns the url for selecting a personality
 */
export const getPersonalitySelectUrl = () => {
  return `${RouteMap.quiz.url}${RouteMap.quiz.personality.url}${RouteMap.quiz.personality.types}`
}

/**
 * Returns the landing page url for sitemaps. The page should list all cities in alphabetical order
 */
export const getSitemapUrl = (pageNumber, pageSize) => {
  let url = `${RouteMap.sitemap.url}${RouteMap.sitemap.locations.url}`
  if (pageNumber) {
    url += '/' + pageNumber
  }
  if (pageSize) {
    url += '/' + pageSize
  }
  return url
}

/**
 * Returns the url for a sitemap for the specific location
 * @param locationText The google places location text that is store in the database
 */
export const getLocationsUrl = (locationText, pageNumber = 1, pageSize) => {
  let url = `${RouteMap.sitemap.url}${RouteMap.sitemap.locations.url}`
  if (locationText) {
    url += '/' + encodeURIComponent(locationText)
  }
  if (pageNumber) {
    url += '/boards/page/' + pageNumber
  }
  if (pageSize) {
    url += '/' + pageSize
  }
  return url
}

export default Object.freeze(RouteMap)
